/**
 * Public Signal Draft Generator V1 - Language Converter
 * 
 * Converts internal technical wording into public product language.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - No mutation of inputs
 */

/**
 * Internal to public language mappings
 */
const LANGUAGE_MAPPINGS: Record<string, string> = {
  // Governance terms
  'governance': 'controls',
  'policy': 'rule',
  'authority': 'permission',
  'boundary': 'limit',
  'enforcement': 'validation',
  'verification': 'confirmation',
  'admissibility': 'eligibility',
  'receipt': 'record',
  'intent': 'purpose',
  'manifest': 'plan',
  'epoch': 'phase',
  'baseline': 'reference',
  'ratchet': 'step',
  'lane': 'track',
  'route': 'path',
  'dispatch': 'execute',
  'execution': 'run',
  'autonomous': 'automated',
  'proposal': 'suggestion',
  'approval': 'review',
  'pivot': 'change direction',
  'strategy': 'approach',
  'simulation': 'test',
  'context': 'situation',
  'reconciliation': 'alignment',
  
  // Technical terms
  'module': 'component',
  'adapter': 'connector',
  'controller': 'manager',
  'validator': 'checker',
  'sanitizer': 'cleaner',
  'generator': 'creator',
  'dispatcher': 'runner',
  'selector': 'chooser',
  'executor': 'worker',
  'handler': 'processor',
  'middleware': 'bridge',
  'pipeline': 'flow',
  'artifact': 'output',
  'bundle': 'package',
  'schema': 'structure',
  'contract': 'agreement',
  'invariant': 'rule',
  'deterministic': 'consistent',
  'fail-closed': 'safe by default',
  'canonical': 'standard',
  'hash': 'identifier',
  'signature': 'stamp',
  
  // Implementation terms
  'closeout': 'completion',
  'integration': 'connection',
  'hardening': 'strengthening',
  'hardmode': 'strict mode',
  'surface': 'interface',
  'scope guard': 'filter',
  'boundary guard': 'limit checker',
  'state machine': 'workflow',
  'transition': 'change',
  'lifecycle': 'process',
  'orchestration': 'coordination',
  'composition': 'combination',
  
  // Testing terms
  'regression': 'stability check',
  'adversarial': 'stress test',
  'verification': 'check',
  'validation': 'confirm',
  'assertion': 'check',
  'mock': 'simulation',
  'fixture': 'sample',
  
  // Performance terms
  'benchmark': 'performance test',
  'baseline': 'reference',
  'metric': 'measurement',
  'threshold': 'limit',
  'latency': 'delay',
  'throughput': 'capacity',
};

/**
 * Convert internal language to public language
 */
export function convertToPublicLanguage(text: string): string {
  let converted = text;

  // Apply mappings in a deterministic order (sorted by key length descending)
  const sortedKeys = Object.keys(LANGUAGE_MAPPINGS).sort((a, b) => b.length - a.length);

  for (const internalTerm of sortedKeys) {
    const publicTerm = LANGUAGE_MAPPINGS[internalTerm];
    // Case-insensitive replacement
    const regex = new RegExp(`\\b${internalTerm}\\b`, 'gi');
    converted = converted.replace(regex, publicTerm);
  }

  return converted;
}

/**
 * Convert closeout artifact field to public language
 */
export function convertCloseoutFieldToPublic(
  fieldName: string,
  value: string
): string {
  return convertToPublicLanguage(value);
}
